import React, { useEffect, useRef, useState } from 'react';
import { queueCount, onQueueChange } from '../../services/defectQueue';

// EQG-MOB-00 §6 — four-state sync chip. 44px pill, 8px dot, two lines
// (label 12.5/700, sub-label mono 10px). Online-only build: real states are
// Synced / Syncing / Offline; "Sync failed" is reserved for a failed manual sync.
const CFG = {
  synced:  { dot: '#1B9E5A', bg: '#E3F3E9', label: 'Synced' },
  syncing: { dot: '#E0B800', bg: '#FFF7D1', label: 'Syncing' },
  offline: { dot: '#C23A00', bg: '#FFE9E0', label: 'Offline' },
  failed:  { dot: '#A81616', bg: '#FFE1E1', label: 'Sync failed' },
};

const SyncStatusChip = () => {
  const [state, setState] = useState(
    typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'synced'
  );
  const [lastSync, setLastSync] = useState(() => new Date());
  const [queued, setQueued] = useState(() => queueCount());
  const timer = useRef(null);

  useEffect(() => {
    const goOnline = () => {
      setState('syncing');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        setState(queueCount() > 0 ? 'syncing' : 'synced');
        setLastSync(new Date());
      }, 900);
    };
    const goOffline = () => setState('offline');
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const off = onQueueChange(() => setQueued(queueCount()));
    const iv = setInterval(() => {
      if (navigator.onLine) setLastSync(new Date());
    }, 60000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      off();
      clearInterval(iv);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const cfg = CFG[state];
  const hhmm = lastSync.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const sub =
    state === 'offline' ? (queued > 0 ? `${queued} queued` : 'No connection')
    : state === 'synced' ? hhmm
    : state === 'syncing' ? (queued > 0 ? `${queued} to send` : 'Updating…')
    : 'Tap to retry';

  return (
    <div
      className="inline-flex items-center gap-2 rounded-[999px] pl-2.5 pr-3 shrink-0"
      style={{ height: 44, background: cfg.bg }}
      data-testid="mobile-sync-chip"
      title={state === 'synced' ? `Last synced ${hhmm}` : cfg.label}
    >
      <span
        className={`rounded-full ${state === 'syncing' ? 'animate-pulse' : ''}`}
        style={{ width: 8, height: 8, background: cfg.dot }}
      />
      <div className="flex flex-col leading-tight">
        <span className="text-[12.5px] font-bold" style={{ color: '#1A1A1A' }}>{cfg.label}</span>
        <span className="font-mono text-[10px]" style={{ color: '#59554D' }}>{sub}</span>
      </div>
    </div>
  );
};

export default SyncStatusChip;
